import { resolveB2cFrontendUrl } from '../shared/seo/siteOrigin';

/**
 * Blog image paths — mirrors public/images/blogs (see supabase/functions/_shared/blogImagePaths.ts).
 */
export const BLOG_IMAGES_PUBLIC_PREFIX = '/images/blogs';
export const BLOG_IMAGES_BUCKET = 'blog-images';

const SITE_URL =
  resolveB2cFrontendUrl()
  || (import.meta.env.VITE_SITE_URL as string | undefined)?.replace(/\/$/, '')
  || 'https://www.gograbjob.com';

const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.replace(
  /\/$/,
  '',
);

function blogImageFileName(slug: string): string {
  const safe = slug.replace(/[^a-z0-9-]/gi, '-').replace(/-+/g, '-').slice(0, 80);
  return `${safe}.png`;
}

/** Supabase CDN URL for a slug hero (works without Netlify static deploy). */
export function blogHeroSupabaseUrl(slug: string, cacheBust?: number): string | null {
  if (!SUPABASE_URL || !slug) return null;
  const v = cacheBust ?? Date.now();
  const key = `images/blogs/${blogImageFileName(slug)}`;
  return `${SUPABASE_URL}/storage/v1/object/public/${BLOG_IMAGES_BUCKET}/${key}?v=${v}`;
}

/** Resolve blog image URL for display (admin preview + public pages). */
export function resolveBlogImageSrc(
  url: string | null | undefined,
  slug?: string | null,
): string | null {
  const localHero = (path: string) => {
    if (import.meta.env.DEV && typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin}${path}`;
    }
    return null;
  };

  if (!url && slug) {
    const path = `${BLOG_IMAGES_PUBLIC_PREFIX}/${blogImageFileName(slug)}?v=${Date.now()}`;
    return localHero(path) ?? blogHeroSupabaseUrl(slug);
  }
  if (!url) return null;

  if (url.startsWith('http://') || url.startsWith('https://')) return url;

  if (url.startsWith('/images/blogs/')) {
    const devLocal = localHero(url);
    if (devLocal) return devLocal;
    const supabase = blogHeroSupabaseUrlFromPath(url);
    if (supabase) return supabase;
    if (typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin}${url}`;
    }
    return `${SITE_URL}${url}`;
  }

  if (url.startsWith('/')) {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return `${window.location.origin}${url}`;
    }
    return `${SITE_URL}${url}`;
  }

  return url;
}

function blogHeroSupabaseUrlFromPath(path: string): string | null {
  if (!SUPABASE_URL) return null;
  const match = path.match(/^\/images\/blogs\/([^?]+)/);
  if (!match) return null;
  const file = match[1];
  const qs = path.includes('?') ? path.slice(path.indexOf('?')) : `?v=${Date.now()}`;
  return `${SUPABASE_URL}/storage/v1/object/public/${BLOG_IMAGES_BUCKET}/images/blogs/${file}${qs}`;
}
